import React from 'react';
import { useDebouncedCallback } from 'use-debounce';

/**
 * Hook that observes the stock item container width to determine if compact mode should be used.
 * Compact mode shows only icons when the container is smaller than the threshold.
 */
export const useStockItemCompact = (threshold = 465) => {
  const [isCompact, setIsCompact] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const debouncedSetIsCompact = useDebouncedCallback((width: number) => {
    setIsCompact(width < threshold);
  }, 100);

  React.useEffect(() => {
    const observeContainer = () => {
      if (!containerRef.current) return;

      // Find the closest stock item container to measure its width
      const stockItemContainer = containerRef.current.closest('.stock-item');
      if (!stockItemContainer) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          debouncedSetIsCompact(width);
        }
      });

      resizeObserver.observe(stockItemContainer);

      // Initial check
      const width = stockItemContainer.getBoundingClientRect().width;
      setIsCompact(width < threshold);

      return () => {
        resizeObserver.disconnect();
      };
    };

    const cleanup = observeContainer();
    return cleanup;
  }, [threshold, debouncedSetIsCompact]);

  return { isCompact, containerRef };
};
